// API service for Swipey.ai authentication endpoints
import { getEfTransactionId } from './everflow'
import { getTrackingParams } from './tracking'
import { setStorage, getStorage, setAuth } from './storage'
import { LP_SESSION_TTL_MS } from './session'
import { resolvePaywallPath, getNavType } from './paywall'
import { getGbCountryCode, getGbDeviceId } from './gbIdentity'
import { getLpSource } from '../config/domainConfig'

const SWIPEY_BASE_URL = process.env.REACT_APP_SWIPEY_BASE_URL

const API_BASE_URL = `${SWIPEY_BASE_URL}/api/v1`
console.log('API_BASE_URL', API_BASE_URL)

const API_KEY = process.env.REACT_APP_API_KEY

// Lander identifier sent to the backend so registrations can be attributed to
// this LP, regardless of whether the user signs up via email/Google/magic-link.
// Resolved from the live hostname (swipeyconnect vs swipeyquiz) — see
// src/config/domainConfig.js.
export const LP_SOURCE = getLpSource()

// Voice label mapping
const voiceLabelMap = {
  Cute: 'Voice 2',
  teen: 'Voice 3',
  Soft: 'Voice 4',
  Dominant: 'Voice 5',
}

// Ethnicity mapping
const ethnicityLabelMap = {
  Arab: 'Mid.Eastern',
}

// Relationship mapping — /quiz display values → existing backend labels.
// We keep the original Step Sister / Step Mom / Girlfriend IDs on the backend; only the UI label changes.
const relationshipLabelMap = {
  gamerBuddy: 'Step Sister',
  closeFriend: 'Step Mom',
  bestFriend: 'Girlfriend',
}

// Attributes map cache
let attributesMap = {}
let attributesLoaded = false

// External-click-id keys cache. The list of keys (e.g. ['twclid','gclid','fbclid','ttclid'])
// is owned by the backend; we fetch it once per page load and reuse it for every auth call.
let externalClickIdKeys = null
let externalClickIdKeysPromise = null

// Snapshot the landing-URL query params into sessionStorage on module load.
// We read externalClickIds from this snapshot — NOT from localStorage `utm_params` (which
// merges across visits and leaks stale click-ids), and NOT from `window.location.search`
// at submit time (Home-flow navigate() strips query params before we get to /signup).
// sessionStorage clears when the tab closes → each tab starts fresh, no cross-session leakage.
const LANDING_URL_PARAMS_KEY = 'landing_url_params'
;(() => {
  if (typeof window === 'undefined') return
  try {
    if (sessionStorage.getItem(LANDING_URL_PARAMS_KEY)) return
    const snapshot = {}
    new URLSearchParams(window.location.search).forEach((value, key) => {
      snapshot[key] = value
    })
    sessionStorage.setItem(LANDING_URL_PARAMS_KEY, JSON.stringify(snapshot))
  } catch {}
})()

// Common headers for all API requests
const getHeaders = () => ({
  'Content-Type': 'application/json',
  'x-api-key': API_KEY,
})

// Generic API request function
const apiRequest = async (endpoint, method = 'GET', body = null) => {
  try {
    const config = {
      method,
      headers: getHeaders(),
    }

    if (body) {
      config.body = JSON.stringify(body)
    }
    console.log('API_BASE_URL!!!', API_BASE_URL)
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config)
    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.message || `HTTP error! status: ${response.status}`)
    }

    return data
  } catch (error) {
    throw error
  }
}

// Load attributes from API
export const loadCharacterAttributes = async () => {
  if (attributesLoaded) return attributesMap

  try {
    const response = await fetch(`${API_BASE_URL}/characters-attributes`, {
      method: 'GET',
      headers: getHeaders(),
    })

    if (!response.ok) {
      throw new Error('❌ Unable to load attributes')
    }

    const result = await response.json()

    result.forEach(attr => {
      if (!attr.type || !attr.label || !attr._id) return
      const type = attr.type.toLowerCase()
      if (!attributesMap[type]) attributesMap[type] = {}

      // Create a key that includes style and gender for better matching
      const key = attr.style && attr.gender ? `${attr.label.trim()}_${attr.style}_${attr.gender}` : attr.label.trim()

      attributesMap[type][key] = attr._id

      // Also store under the original label for backward compatibility
      attributesMap[type][attr.label.trim()] = attr._id
    })

    attributesLoaded = true
    return attributesMap
  } catch (err) {
    return {}
  }
}

// Helper function to get attribute ID with style and gender matching
export const getAttributeId = (type, label, userStyle, userGender) => {
  if (!attributesMap[type] || !label) return null

  // Handle voice mapping
  if (type === 'voice' && voiceLabelMap[label]) {
    label = voiceLabelMap[label]
  }

  // Handle ethnicity mapping
  if (type === 'ethnicity' && ethnicityLabelMap[label]) {
    label = ethnicityLabelMap[label]
  }

  // Handle relationship mapping (/quiz custom values → backend labels)
  if (type === 'relationship' && relationshipLabelMap[label]) {
    label = relationshipLabelMap[label]
  }

  // First try to find with exact style and gender match
  if (userStyle && userGender) {
    const styleKey = `${label.trim()}_${userStyle}_${userGender}`
    if (attributesMap[type][styleKey]) {
      return attributesMap[type][styleKey]
    }
  }

  // Fallback to original label matching
  return attributesMap[type][label.trim()] || null
}

const apiToCamelCaseMap = {
  ethnicity: 'ethnicity',
  age: 'ageAttribute',
  eyecolor: 'eyeColor',
  hairstyle: 'hairStyle',
  haircolor: 'hairColor',
  bodytype: 'bodyType',
  breastsize: 'breastSize',
  buttsize: 'buttSize',
  personality: 'personality',
  occupation: 'occupation',
  hobbies: 'hobbies',
  relationship: 'relationship',
  voice: 'defaultVoice',
}

// Convert quiz selections to API format
export const convertSelectionsToApiFormat = async selections => {
  // Ensure attributes are loaded
  await loadCharacterAttributes()

  const userAnswers = {
    gender: 'Girls',
    style: selections?.welcome === 'Anime' ? 'Anime' : 'Realistic',
    attributes: {},
  }

  const mapAgeToApiLabel = age => {
    if (age >= 18 && age <= 19) return '18+'
    if (age >= 20 && age <= 29) return '20s'
    if (age >= 30 && age <= 39) return '30s'
    return '40+'
  }

  // Step1: ethnicity, age
  if (selections?.step1) {
    if (selections.step1.ethnicity) {
      const id = getAttributeId('ethnicity', selections.step1.ethnicity)
      if (id) userAnswers.attributes[apiToCamelCaseMap.ethnicity] = id
    }
    if (selections.step1.age) {
      const ageId = getAttributeId('age', mapAgeToApiLabel(selections.step1.age))
      if (ageId) userAnswers.attributes[apiToCamelCaseMap.age] = ageId
    }
  }

  // Step2: body attributes
  if (selections?.step2) {
    const step2Map = {
      bodyType: 'bodytype',
      breastSize: 'breastsize',
      buttSize: 'buttsize',
    }
    Object.entries(step2Map).forEach(([key, apiKey]) => {
      if (selections.step2[key]) {
        const id = getAttributeId(apiKey, selections.step2[key])
        if (id) userAnswers.attributes[apiToCamelCaseMap[apiKey]] = id
      }
    })
  }

  // Step3: hair, eyes
  if (selections?.step3) {
    const step3Map = {
      hairColor: 'haircolor',
      hairStyle: 'hairstyle',
      eyeColor: 'eyecolor',
    }
    Object.entries(step3Map).forEach(([key, apiKey]) => {
      if (selections.step3[key]) {
        const id = getAttributeId(apiKey, selections.step3[key])
        if (id) userAnswers.attributes[apiToCamelCaseMap[apiKey]] = id
      }
    })
  }

  // Step4: personality, voice
  if (selections?.step4) {
    if (selections.step4.personality) {
      const ids = Array.isArray(selections.step4.personality)
        ? selections.step4.personality.map(p => getAttributeId('relationship', p)).filter(Boolean)
        : [getAttributeId('relationship', selections.step4.personality)].filter(Boolean)

      if (ids.length > 0) userAnswers.attributes[apiToCamelCaseMap.relationship] = ids.length > 1 ? ids : ids[0]
    }

    // /quiz adds an extra personality-trait selection (Caregiver / Jester / Innocent)
    if (selections.step4.personalityTrait) {
      const traitValue = selections.step4.personalityTrait
      const traitLabel =
        selections.step4.personalityTraitLabel ||
        (typeof traitValue === 'string' ? traitValue.charAt(0).toUpperCase() + traitValue.slice(1) : null)

      const personalityId =
        (traitLabel && getAttributeId('personality', traitLabel)) ||
        getAttributeId('personality', traitValue)

      if (personalityId) userAnswers.attributes[apiToCamelCaseMap.personality] = [personalityId]
    }

    if (selections.step4.voice) {
      const voiceId = getAttributeId('voice', selections.step4.voice)
      if (voiceId) userAnswers.attributes[apiToCamelCaseMap.voice] = voiceId
    }
  }

  return userAnswers
}

// Create model-params from selections
export const createModelParams = async selections => {
  const userAnswers = await convertSelectionsToApiFormat(selections)

  const modelParamsObj = {
    formBody: {
      style: userAnswers.style,
      gender: userAnswers.gender,
      attributes: { ...userAnswers.attributes },
      defaultVoice: null,
    },
  }

  const json = JSON.stringify(modelParamsObj)
  const base64 = btoa(unescape(encodeURIComponent(json)))

  return base64
}

// Add model params to URL
export const addModelParamsToUrl = (baseUrl, modelParams, additionalParams = {}) => {
  const url = new URL(baseUrl)

  // Swap the backend-provided destination to /lobby when the nav config asks for chat_paywall,
  // while keeping every query param — including model-params — intact. Leave the path untouched
  // for the default 'paywall' type so existing behaviour is preserved.
  const navType = getNavType()
  if (navType === 'chat_paywall') {
    url.pathname = resolvePaywallPath(navType)
    // Tag the /lobby handoff so the lobby flow/analytics can identify the source.
    url.searchParams.set('type', 'chat_paywall')
  }

  // Add model-params
  url.searchParams.set('model-params', modelParams)
  // create-friend-white is the SFW lander variant — every paywall handoff carries protection=sfw.
  url.searchParams.set('protection', 'sfw')

  // Add additional parameters
  Object.entries(additionalParams).forEach(([key, value]) => {
    if (value) {
      url.searchParams.set(key, value)
    }
  })

  // Add current URL params
  const currentUrlParams = new URLSearchParams(window.location.search)
  for (const [key, value] of currentUrlParams.entries()) {
    if (!url.searchParams.has(key)) {
      url.searchParams.set(key, value)
    }
  }

  return url.toString()
}

// Fetch the list of click-id keys the backend wants us to forward. Cached for the page lifetime;
// in-flight requests are deduped via `externalClickIdKeysPromise`. Failures / timeouts fall back
// to [] so auth + OAuth redirects can't be blocked by this endpoint — externalClickIds gets sent as {}.
const EXTERNAL_CLICK_ID_KEYS_TIMEOUT_MS = 3000

const fetchExternalClickIdKeys = async () => {
  if (Array.isArray(externalClickIdKeys)) return externalClickIdKeys
  if (externalClickIdKeysPromise) return externalClickIdKeysPromise

  externalClickIdKeysPromise = (async () => {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
    const timeoutId = controller
      ? setTimeout(() => controller.abort(), EXTERNAL_CLICK_ID_KEYS_TIMEOUT_MS)
      : null
    try {
      const response = await fetch(`${API_BASE_URL}/marketing/external-click-id-keys`, {
        method: 'GET',
        headers: getHeaders(),
        signal: controller?.signal,
      })
      if (!response.ok) {
        externalClickIdKeys = []
        return externalClickIdKeys
      }
      const data = await response.json()
      externalClickIdKeys = Array.isArray(data) ? data : []
      return externalClickIdKeys
    } catch (err) {
      console.warn('[externalClickIds] keys fetch failed, falling back to []', err)
      externalClickIdKeys = []
      return externalClickIdKeys
    } finally {
      if (timeoutId) clearTimeout(timeoutId)
      externalClickIdKeysPromise = null
    }
  })()

  return externalClickIdKeysPromise
}

// Build the externalClickIds object the backend expects: for each key returned by
// /marketing/external-click-id-keys, pull the value from this tab's landing-URL snapshot.
// Source rationale (see LANDING_URL_PARAMS_KEY comment above):
//   - NOT localStorage.utm_params → leaks click-ids from previous visits
//   - NOT window.location.search at submit time → SPA navigate() strips it on Home flow
// Snapshot was taken once on module load, so this is the URL the user actually arrived with.
export const getExternalClickIds = async () => {
  const keys = await fetchExternalClickIdKeys()
  let landingParams = {}
  try {
    const raw = sessionStorage.getItem(LANDING_URL_PARAMS_KEY)
    if (raw) landingParams = JSON.parse(raw) || {}
  } catch {
    landingParams = {}
  }

  return keys.reduce((acc, key) => {
    const value = landingParams[key]
    acc[key] = value == null || value === '' ? null : value
    return acc
  }, {})
}

// UTM data management - now using our tracking system
export const collectAndStoreUTMData = () => {
  // Initialize tracking if not already done
  if (typeof window !== 'undefined' && window.initTracking) {
    window.initTracking()
  }

  const trackingParams = getTrackingParams()

  // Also get current URL params directly as fallback
  const currentUrlParams = new URLSearchParams(window.location.search)
  const currentParams = {}
  for (const [key, value] of currentUrlParams.entries()) {
    currentParams[key] = value
  }
  const utmObj = {
    ...trackingParams,
    ...currentParams, // Include current URL params as fallback
    path: '/paywall',
  }

  // Store UTM data using our storage utility
  setStorage('utm_data', utmObj)
  return utmObj
}

// Enhanced auth callback handler with model params
export const handleAuthCallback = async (selections = null) => {
  // /quiz handles its own Google auth callback so the user stays inside the quiz flow.
  if (typeof window !== 'undefined' && window.location.pathname.includes('/quiz')) {
    return false
  }
  const params = new URLSearchParams(window.location.search)
  const authToken = params.get('auth_token')
  const authType = params.get('auth_type')

  if (authToken && authType) {
    const utm = getStorage('utm_data') || {}
    // GrowthBook isn't initialized yet on the OAuth return, so the nav type comes from the
    // value GrowthBookProvider persisted to localStorage before redirecting out.
    const navType = getNavType()
    const paywallUrl = new URL(`${SWIPEY_BASE_URL}${resolvePaywallPath(navType)}`)

    if (selections?.welcome) {
      const typeValue = selections.welcome === 'Anime' ? 'anime' : 'realistic'
      paywallUrl.searchParams.set('type', typeValue)
    }

    paywallUrl.searchParams.set('auth_token', authToken)
    paywallUrl.searchParams.set('auth_type', authType)
    // create-friend-white is the SFW lander variant — every paywall handoff carries protection=sfw.
    paywallUrl.searchParams.set('protection', 'sfw')
    paywallUrl.searchParams.set('lp_source', LP_SOURCE)

    // Add type parameter based on selections style
    if (selections?.welcome) {
      const typeValue = selections.welcome === 'Anime' ? 'anime' : 'realistic'
      paywallUrl.searchParams.set('type', typeValue)
    }

    // Add UTM params
    Object.entries(utm).forEach(([key, value]) => {
      if (value) paywallUrl.searchParams.set(key, value)
    })

    // Add model params if selections are available
    if (selections) {
      try {
        const modelParams = await createModelParams(selections)
        paywallUrl.searchParams.set('model-params', modelParams)
        // Model params added successfully
      } catch (error) {
        // Error adding model params
      }
    }

    // chat_paywall → /lobby: set type=chat_paywall LAST so it wins over the style type and the
    // utm spread above (which forwards the landing URL's ?type=). /lobby uses model-params.
    if (navType === 'chat_paywall') paywallUrl.searchParams.set('type', 'chat_paywall')

    window.location.replace(paywallUrl.toString())
    return true // Indicates callback was handled
  }
  return false // No callback to handle
}

// Account creation API with model params
export const createAccount = async (email, password, selections = null) => {
  const ef_transaction_id = getEfTransactionId()
  const payload = { email, password, ef_transaction_id }

  const data = await apiRequest('/auth/account', 'POST', payload)

  if (data.success && data.redirectUrl && selections) {
    try {
      const modelParams = await createModelParams(selections)
      data.redirectUrl = addModelParamsToUrl(data.redirectUrl, modelParams)
      // Model params added successfully
    } catch (err) {
      // Error adding model params
    }
  }

  return data
}

// Paywall-popup flow helper, shared by the password (createPreLanderAccount) and
// magic-link (submitEmailOnly) registration paths so BOTH send the same extras.
//
// For /lobby to drive the popup → Upgate → chat/generation flow, the backend
// needs the popup params in the registration Redis payload. The DTO whitelist
// strips unknown top-level fields, so subscription_required/type/flow are nested
// inside `ab_testing` (typed {[k]:string}, forwarded verbatim into Redis).
// model_params IS a typed top-level DTO field, so it's returned separately.
//
// Returns { abTesting, modelParams } — abTesting always defined; modelParams only
// for the paywall_popup case (undefined otherwise).
// GrowthBook bucketing identity for the registration body, popup_first arm ONLY.
//
// That arm is the one where the lander itself resolves flags the backend then CHARGES
// on (the geo price multiplier, the rebill arm), so the two have to bucket
// identically. Every other arm hands the visitor to the platform paywall, which
// evaluates in its own first-party context — sending the id there would seed
// Lead.gb_device_id (write-once) from the lander and pin the platform to the lander's
// bucket for good. Both nav type AND order are checked: `order` is resolved from the
// nav-config flag even for non-popup types, and on those the visitor DOES go to the
// platform paywall.
//
// `country_code` is a FALLBACK only — the backend resolves the country from the
// request IP and that wins; this just covers its lookup coming back empty.
//
// Each key is omitted when unresolved, so a failed lookup leaves the backend on its
// own geo rather than receiving an empty string. Returns {} when the arm does not
// apply, so it can be spread into a payload unconditionally.
const gbBucketingFields = ab_testing => {
  const isPopupFirst =
    ab_testing?.['createsexfriends-white-paywall-nav-config'] === 'paywall_popup' &&
    ab_testing?.['createsexfriends-white-paywall-order'] === 'popup_first'
  if (!isPopupFirst) return {}
  const gb_device_id = getGbDeviceId()
  const country_code = getGbCountryCode()
  return {
    ...(gb_device_id ? { gb_device_id } : {}),
    ...(country_code ? { country_code } : {}),
  }
}

const buildPaywallPopupRegistrationExtras = async (ab_testing, selections) => {
  const navType = ab_testing?.['createsexfriends-white-paywall-nav-config']
  if (navType !== 'paywall_popup') {
    return { abTesting: ab_testing, modelParams: undefined }
  }
  const abTesting = {
    ...ab_testing,
    subscription_required: 'true',
    type: 'paywall_popup',
    flow: ab_testing?.['createsexfriends-white-paywall-flow'] || 'generation',
    // Which side of the signup screen the popup was shown on (see the nav-config
    // doc block in GrowthBookContext). Nested here too so the backend replays it in
    // the marketing payload / Mixpanel props, not just as a GB experiment key.
    order: ab_testing?.['createsexfriends-white-paywall-order'] || 'after_signup',
  }
  let modelParams
  if (selections) {
    try {
      modelParams = await createModelParams(selections)
    } catch (err) {
      console.error('Error creating model_params for paywall popup', err)
    }
  }
  return { abTesting, modelParams }
}

// Enhanced pre-lander account creation API with model params
export const createPreLanderAccount = async (email, password, selections = null, ab_testing) => {
  const ef_transaction_id = getEfTransactionId()
  const utm = getStorage('utm_data') || {}
  const externalClickIds = await getExternalClickIds()

  const { abTesting: abTestingPayload, modelParams: modelParamsField } =
    await buildPaywallPopupRegistrationExtras(ab_testing, selections)

  const body = {
    ...utm,
    email,
    password,
    ef_transaction_id,
    ...gbBucketingFields(ab_testing),
    ab_testing: abTestingPayload,
    externalClickIds,
    lp_source: LP_SOURCE,
    ...(modelParamsField ? { model_params: modelParamsField } : {}),
  }
  console.log('[auth] POST /auth/pre-lander body →', body)
  const data = await apiRequest('/auth/pre-lander', 'POST', body)

  if (data.message && data.message.includes('User with this email already exists')) {
    return {
      success: false,
      error: 'Email already used',
    }
  }

  // If successful, add model params to any redirect URL
  if (data.success && data.redirectUrl && selections) {
    const modelParams = await createModelParams(selections)
    data.redirectUrl = addModelParamsToUrl(data.redirectUrl, modelParams)
  }

  return data
}

// simple_registration AB test (86ca50q0c): create a dummy user WITHOUT collecting
// an email/password on the LP. Mirrors createPreLanderAccount's body (the full
// attribution the backend replays later at checkout) minus email/password.
// Returns { success, authToken, loginUrl }.
export const createTempSimpleRegistration = async (selections = null, ab_testing) => {
  const ef_transaction_id = getEfTransactionId()
  const utm = getStorage('utm_data') || {}
  const externalClickIds = await getExternalClickIds()

  const { abTesting: abTestingPayload, modelParams: modelParamsField } =
    await buildPaywallPopupRegistrationExtras(ab_testing, selections)

  return apiRequest('/auth/pre-lander-temp-simple-registration', 'POST', {
    ...utm,
    ef_transaction_id,
    ...gbBucketingFields(ab_testing),
    ab_testing: abTestingPayload,
    externalClickIds,
    lp_source: LP_SOURCE,
    ...(modelParamsField ? { model_params: modelParamsField } : {}),
  })
}

// Enhanced pre-lander login API with model params
export const loginPreLander = async (email, password, selections = null) => {
  const ef_transaction_id = getEfTransactionId()
  const utm = getStorage('utm_data') || {}
  const externalClickIds = await getExternalClickIds()

  const payload = {
    ...utm,
    email,
    password,
    ef_transaction_id,
    externalClickIds,
    lp_source: LP_SOURCE,
  }

  const data = await apiRequest('/auth/pre-lander', 'POST', payload)

  // Add model params to any redirect URL if selections are available
  if (data.success && data.redirectUrl && selections) {
    const modelParams = await createModelParams(selections)
    data.redirectUrl = addModelParamsToUrl(data.redirectUrl, modelParams, {
      auth_token: data.authToken,
      auth_type: 'user',
    })
    // Enhanced login redirect URL with model params
  }

  return data
}

// Enhanced complete login flow handler with model params
export const handleLoginFlow = async (email, password, selections) => {
  try {
    const ef_transaction_id = getEfTransactionId()
    const utm = getStorage('utm_data') || {}
    const externalClickIds = await getExternalClickIds()

    const body = {
      ...utm,
      email,
      password,
      ef_transaction_id,
      externalClickIds,
      lp_source: LP_SOURCE,
    }
    console.log('[auth] POST /auth/pre-lander/login body →', body)
    const data = await apiRequest('/auth/pre-lander/login', 'POST', body)

    const authToken = data.authToken || data.token
    const user = data

    if (authToken) {
      // Legacy mirror (nothing in this LP reads it back). Expire it with the token
      // it copies — the backend drops external_registration:<token> after an hour,
      // so there's no reason for a spent token to outlive that in localStorage.
      setAuth(authToken, null, LP_SESSION_TTL_MS)

      const isSubscribed = user?.isSubscribed === true

      const isPw = window.location.pathname.includes('/pw')

      if (isSubscribed) {
        const homeUrl = new URL(`${SWIPEY_BASE_URL}/`)
        homeUrl.searchParams.set('auth_token', authToken)
        homeUrl.searchParams.set('auth_type', 'login_pre_lander')
        // create-friend-white is the SFW lander variant — every handoff carries protection=sfw.
        homeUrl.searchParams.set('protection', 'sfw')
        homeUrl.searchParams.set('lp_source', LP_SOURCE)

        Object.entries(utm).forEach(([key, value]) => {
          if (value) homeUrl.searchParams.set(key, value)
        })
        if (isPw) {
          homeUrl.searchParams.set('media', 'vid.mp4')
          homeUrl.searchParams.set('pw_v', '2')
        }

        return {
          success: true,
          redirectUrl: homeUrl.toString(),
        }
      } else {
        const navType = getNavType()
        const paywallUrl = new URL(`${SWIPEY_BASE_URL}${resolvePaywallPath(navType)}`)
        paywallUrl.searchParams.set('auth_token', authToken)
        paywallUrl.searchParams.set('auth_type', 'login_paywall')
        // create-friend-white is the SFW lander variant — every paywall handoff carries protection=sfw.
        paywallUrl.searchParams.set('protection', 'sfw')
        paywallUrl.searchParams.set('lp_source', LP_SOURCE)

        if (selections?.welcome) {
          const typeValue = selections.welcome === 'Anime' ? 'anime' : 'realistic'
          paywallUrl.searchParams.set('type', typeValue)
        }

        Object.entries(utm).forEach(([key, value]) => {
          if (value) paywallUrl.searchParams.set(key, value)
        })

        if (selections) {
          try {
            const modelParams = await createModelParams(selections)
            paywallUrl.searchParams.set('model-params', modelParams)
          } catch (err) {
            console.error('Error adding model-params', err)
          }
        }
        if (isPw) {
          paywallUrl.searchParams.set('media', 'vid.mp4')
          paywallUrl.searchParams.set('pw_v', '2')
        }

        // chat_paywall → /lobby: set type=chat_paywall LAST so it wins over the style type and the
        // utm spread above (which forwards the landing URL's ?type=). /lobby uses model-params.
        if (navType === 'chat_paywall') paywallUrl.searchParams.set('type', 'chat_paywall')

        return {
          success: true,
          redirectUrl: paywallUrl.toString(),
        }
      }
    }

    return {
      success: false,
      message: 'Please check your email or password',
    }
  } catch (error) {
    return {
      success: false,
      message: 'Please check your email or password',
    }
  }
}

// ---- Magic-link / email-only OTP flow (createsexfriends-white-magicLink = ON) ----

// Decorate the loginUrl returned by the email-only / OTP-verify endpoints with
// model-params, type, and the SFW protection marker so the post-OAuth/post-OTP
// paywall is reached with the same context the password flow uses. White is the
// SFW lander variant — protection=sfw is added unconditionally.
const decorateEmailOnlyRedirect = async (loginUrl, selections) => {
  if (!loginUrl) return null

  const url = new URL(loginUrl)

  // Redirect to /lobby instead of /paywall when the nav config asks for chat_paywall;
  // model-params is appended below either way. Leave the path alone for the default 'paywall'.
  const navType = getNavType()
  if (navType === 'chat_paywall') url.pathname = resolvePaywallPath(navType)

  url.searchParams.set('protection', 'sfw')

  if (selections?.welcome) {
    url.searchParams.set('type', selections.welcome === 'Anime' ? 'anime' : 'realistic')
  }

  if (selections) {
    try {
      const modelParams = await createModelParams(selections)
      url.searchParams.set('model-params', modelParams)
    } catch (err) {
      console.error('Error adding model-params to magic-link redirect', err)
    }
  }

  const path = typeof window !== 'undefined' ? window.location.pathname : ''
  if (path.includes('/pw')) {
    url.searchParams.set('media', 'vid.mp4')
    url.searchParams.set('pw_v', '2')
  }

  // Forward the landing tracking params (twclid, gclid, utm_*, etc.) onto the paywall
  // redirect so magic-link matches the password flow. We read the stored utm_data
  // snapshot (the same source handleLoginFlow / handleAuthCallback use) — NOT
  // window.location.search, which SPA navigate({replace:true}) strips before submit.
  const utm = getStorage('utm_data') || {}
  Object.entries(utm).forEach(([key, value]) => {
    if (value && !url.searchParams.has(key)) {
      url.searchParams.set(key, value)
    }
  })
  // Click-ids (twclid, gclid, fbclid, ttclid) come from the landing-URL snapshot the
  // codebase trusts for them (utm_data is not a reliable source for click-ids).
  const forwardClickIds = await getExternalClickIds()
  Object.entries(forwardClickIds).forEach(([key, value]) => {
    if (value && !url.searchParams.has(key)) {
      url.searchParams.set(key, value)
    }
  })

  // chat_paywall → /lobby: tag the handoff last so it wins over the style type set above
  // (the lobby reads style from model-params, so the style tag is unused there).
  if (navType === 'chat_paywall') url.searchParams.set('type', 'chat_paywall')

  return url.toString()
}

// POST /auth/pre-lander-with-email-only — magic-link signup/login.
// Existing email → { otp_sent: true, otpExpiry }; new email → { authToken, loginUrl }.
export const submitEmailOnly = async (email, { isResend = false, selections, ab_testing } = {}) => {
  const utm = getStorage('utm_data') || {}
  const ef_transaction_id = getEfTransactionId()
  const externalClickIds = await getExternalClickIds()

  // Same paywall-popup extras as the password flow: for a NEW email this endpoint
  // registers the user via the same path, so /lobby needs model_params +
  // subscription_required/type/flow in the Redis payload to drive the popup flow.
  const { abTesting: abTestingPayload, modelParams: modelParamsField } =
    await buildPaywallPopupRegistrationExtras(ab_testing, selections)

  const payload = {
    ...utm,
    email,
    ef_transaction_id,
    ...gbBucketingFields(ab_testing),
    ab_testing: abTestingPayload,
    externalClickIds,
    isResend,
    lp_source: LP_SOURCE,
    ...(modelParamsField ? { model_params: modelParamsField } : {}),
  }

  const data = await apiRequest('/auth/pre-lander-with-email-only', 'POST', payload)

  if (data?.success === false) return data

  if (data?.authToken && data?.loginUrl) {
    const redirectUrl = await decorateEmailOnlyRedirect(data.loginUrl, selections)
    if (redirectUrl) data.redirectUrl = redirectUrl
    if (data.authToken) setAuth(data.authToken, null, LP_SESSION_TTL_MS) // legacy mirror, see above
  }

  return data
}

// POST /auth/pre-lander/login-with-otp — verify the OTP, get authToken + loginUrl.
export const verifyPreLanderOtp = async (email, otp, { selections } = {}) => {
  const data = await apiRequest('/auth/pre-lander/login-with-otp', 'POST', { email, otp })

  if (data?.success === false) return data

  const authToken = data?.authToken || data?.token
  const loginUrl = data?.loginUrl

  if (authToken) setAuth(authToken, null, LP_SESSION_TTL_MS) // legacy mirror, see above

  if (loginUrl) {
    const redirectUrl = await decorateEmailOnlyRedirect(loginUrl, selections)
    if (redirectUrl) data.redirectUrl = redirectUrl
  }

  return data
}

export const getGoogleAuthUrl = () => `${SWIPEY_BASE_URL}/api/v1/auth/google/login`

// Swipey base URL
export const getSwipeyBaseUrl = () => SWIPEY_BASE_URL

// Export API configuration for debugging
export const getApiConfig = () => ({
  baseUrl: API_BASE_URL,
  swipeyBaseUrl: SWIPEY_BASE_URL,
  googleAuthUrl: getGoogleAuthUrl(),
  hasApiKey: !!API_KEY && API_KEY !== 'your-api-key-here',
  attributesLoaded,
  attributesMap,
})

// ---- Add to your API service file ----

// Request password reset (send code)
export const requestPasswordReset = async email => {
  return apiRequest('/auth/reset-password', 'POST', { email })
}

// Verify OTP for password reset
export const verifyPasswordOtp = async (email, otp) => {
  return apiRequest('/auth/verify-reset-password-otp', 'POST', { email, otp })
}

// Set new password using token
export const updatePassword = async (token, newPassword) => {
  return apiRequest(`/auth/set-new-password?token=${token}`, 'POST', {
    newPassword,
  })
}
